import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/css';
import { useApp } from '../../context/app-context';
import DeliveryAddressTile from './DeliveryAddressTile';
import { PreferencesContext, PreferencesContextType } from './PreferencesContext';
import { invoke, get, find, isEmpty, has, capitalize, includes, lowerCase, cond } from 'lodash';
import DeliveryTabForm from './DeliveryTabForm';
import SlotPreferences from '../SlotPreferences/SlotPreferences';
import PropTypes from 'prop-types';
import UserMessage from '../../common/UserMessage/UserMessage';
import Loader from '../../common/Loading/loader';
import { AddressDataType } from '../../service/save-address-service';
import getSlotsInfo from '../../service/get-slots-service';
import AppLabels from '../../utils/appLabels';
import { ModalContext, ModalContextType } from '../../common/Modal/Modal';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';
import { Address } from '../../service/stores-by-address-service';

const generalStyle = css`
  display: flex;
  flex-direction: column;
  padding-top: 1rem;
`;

const addressListStyle = css`
  display: flex;
  width: 57rem;
  flex-wrap: wrap;
  overflow-y: auto;

  @media (max-width: 641px) {
    flex-direction: column;
    flex-wrap: initial;
    width: auto;
    height: 25rem;
  }
`;

const footerSectionStyle = css`
  display: flex;
  flex-direction: row-reverse;
  margin-top: auto;
`;

const submitButtonStyle = css`
  cursor: pointer;
  padding: 1rem 0.75rem;
  min-width: 16rem;
  border-radius: 6.25rem;
  background: #7b1a27;
  color: #fff;
  border: none;
  font-size: 1rem;
  line-height: 1.5rem;

  &[disabled] {
    background: #f1f0ee;
    color: #535250;
    cursor: initial;
    border: none;
  }

  @media (max-width: 641px) {
    width: 100%;
  }
`;

const addAddressSectionStyle = css`
  display: flex;
  margin-top: 1.5rem;
  color: #7b1a27;
  cursor: pointer;
  background: white;
  border: none;
  width: fit-content;

  @media (max-width: 641px) {
    justify-content: center;
  }
`;

const DeliveryAddressList = ({ onSave }) => {
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const { appData, showToast, setGlobalAriaLiveMessage } = useApp();
  const [addressList, setAddressList] = useState<object[]>([]);
  const preferenceContext = useContext(PreferencesContext) as PreferencesContextType;
  const modalContext = useContext(ModalContext) as ModalContextType;
  const [addingNewAddress, setAddingNewAddress] = useState(false);
  const [updatingAddress, setUpdatingAddress] = useState<object | null>(null);
  const [selectedAddress, setSelectedAddress] = useState({});
  const [savingChanges, setSavingChanges] = useState(false);
  const [slotsInfo, setSlotsInfo] = useState({});
  const [addressValidationError, setAddressValidationError] = useState('');
  const [addressValidationResponse, setAddressValidationResponse] = useState<any>(null);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
  const [addressStoreId, setAddressStoreId] = useState('');
  const currentPreferencesContext = useContext(PreferencesContext) as PreferencesContextType;
  let currentTab: string = '';

  const getDeliveryAddressList = async () => {
    setIsLoadingAddresses(true);

    try {
      const resp = await invoke(preferenceContext, 'fetchAllAddresses');

      setAddressList(get(resp, 'data', {}));
      setIsLoadingAddresses(false);
    } catch (err) {
      invoke(modalContext, 'setModalError', AppLabels.DeliveryTabForm.LOADING_ERROR);
    }
  };

  useEffect(() => {
    if (user && user.authToken) {
      getDeliveryAddressList();
    }
  }, [user]);

  const selectedDeliveryAddress = find(get(addressList, 'selectedAddresses', []), {
    purposes: [{ name: 'AR_DELIVERY' }],
    banner: get(appData, 'banner', '').toUpperCase(),
  });

  const analyticsEvents = () => {
    try {
      if (window?.['digitalData']) {
        window['digitalData'].pageFlags.preferences = 'delivery';
        window['digitalData'].modal.modalMessage = 'change-in-delivery-fullfillment';
      }
      if (window?.['eddlLoadComplete']) {
        window?.['AB']['DATALAYER'].setModalClickEvents('ar:fulfillment-edit|delivery-save');
        window['digitalData'].modal.modalMessage = '';
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const addresses = get(addressList, 'addresses', []).map((address, index) => {
    const fulfillmentIsDelivery =
      get(currentPreferencesContext, 'preferencesData.fulfillmentTypes.0.value') === 'DELIVERY';
    const selected = get(selectedDeliveryAddress, 'addressId', '') === get(address, 'addressId', '');
    return (
      <div key={get(address, 'addressId', '')}>
        <DeliveryAddressTile
          address={address}
          selected={selected && fulfillmentIsDelivery}
          highlighted={get(selectedAddress, 'addressId') === get(address, 'addressId', '')}
          onUseAddress={(address) => {
            if (get(selectedAddress, 'addressId') === get(address, 'addressId')) {
              setSelectedAddress({});
            } else {
              setSelectedAddress(address);
            }
          }}
          onUpdateAddress={(address) => {
            setUpdatingAddress(address);
          }}
          onDeleteAddress={async (address) => {
            try {
              const resp = await invoke(preferenceContext, 'deleteAddress', get(address, 'addressId'));
              if (get(resp, 'status') === 204) {
                showToast('info', 'Address successfully deleted.');
                getDeliveryAddressList();
              } else {
                showToast('error', AppLabels.DeliveryTabForm.DeleteAddressError);
              }
            } catch (err) {
              showToast('error', AppLabels.DeliveryTabForm.DeleteAddressError);
            }
          }}
        />
      </div>
    );
  });

  const fetchSlotsInfo = async (): Promise<void> => {
    setSlotsInfo({ loading: true });

    try {
      const storeID: string =
        selectedAddress && has(selectedAddress, 'stores')
          ? get(selectedAddress, 'stores.0.storeId', '')
          : addressStoreId;

      if (!isEmpty(storeID)) {
        const resp = await invoke(currentSessionContext, 'ensureActiveSession', () =>
          getSlotsInfo(appData.environmentVariables, user, storeID),
        );
        setSlotsInfo({ data: resp.data, loading: false });
      } else {
        setSlotsInfo({ error: true, loading: false });
      }
    } catch (err) {
      setSlotsInfo({ error: err, loading: false });
    }
  };

  // Overriding updateSlotsInfo which is currently the one triggered on the slots save button
  const modifyAddress = () => {
    const addressType = get(selectedAddress, 'addressType.description', '') || get(selectedAddress, 'addressType', '');
    const normalizedAddressType = /^residential/i.test(addressType) ? 'RESIDENTIAL' : 'BUSINESS';
    console.log('modifyAddress called with selectedAddress:', selectedAddress, 'normalizedAddressType:', normalizedAddressType);

    if (get(selectedAddress, 'addressId')) {
      return {
        addressId: get(selectedAddress, 'addressId', ''),
        address1: get(selectedAddress, 'address1', ''),
        address2: get(selectedAddress, 'address2', ''),
        addressType: normalizedAddressType,
        city: get(selectedAddress, 'city', ''),
        state: get(selectedAddress, 'state', ''),
        zipCode: get(selectedAddress, 'zipCode', ''),
      };
    } else {
      return {
        addressId: get(selectedAddress, 'addressId', ''),
        address1: get(selectedAddress, 'address1', ''),
        address2: get(selectedAddress, 'address2', ''),
        addressType: normalizedAddressType,
        city: get(selectedAddress, 'city', ''),
        notes: [
          {
            type: 'DELIVERY_INSTRUCTIONS',
          },
          {
            type: 'DELIVERY_TYPE',
            value: 'UNATTENDED',
          },
        ],
        state: get(selectedAddress, 'state', ''),
        zipCode: get(selectedAddress, 'zipCode', ''),
      };
    }
  };

  const updateSlotsInfo = async (slotsInfo: any): Promise<any> => {
    const addressData: AddressDataType = {
      guid: user.guid,
      address: modifyAddress(),
      fulfillmentType: {
        preferenceDefId: '',
        value: 'DELIVERY',
      },
      slotPreference: {
        slotId: get(slotsInfo, 'slotId', ''),
        fulfillmentDay: get(slotsInfo, 'fulfillmentDay', ''),
        slotStartTS: get(slotsInfo, 'slotStartTS', ''),
        slotEndTS: get(slotsInfo, 'slotEndTS', ''),
        slotType: get(slotsInfo, 'slotType', ''),
        preferenceDefId: '',
      },
    };

    try {
      await invoke(preferenceContext, 'saveAddress', addressData);
      await invoke(preferenceContext, 'fetchPreferences');
      showToast('info', 'You have successfully updated your preferences.');

      // only call when save is successful
      onSave();
    } catch (err) {
      invoke(modalContext, 'setModalError', AppLabels.DeliveryTabForm.LOADING_ERROR);
    }
  };

  const setFormValidationError = (errorMessage: string) => {
    setAddressValidationError(errorMessage);

    if (!isEmpty(errorMessage)) {
      setGlobalAriaLiveMessage(`error, ${errorMessage}`);
    }
  };

  const getValidationPayload = (address, existingPurposes = null) => ({
    address1: address.streetAddress,
    address2: address.aptSuite,
    addressType: { name: address.placeType === 'Residential' ? '1' : '2' },
    city: address.city,
    purposes: existingPurposes,
    state: address.state,
    zipCode: address.zipCode,
    addressId: address.addressId || '',
    addressValidationStatus: {},
    zipCodeExtn: '',
    geoCodes: {
      latitude: '',
      longitude: '',
    },
  });

  const handleServiceError = (err) => {
    const responseStatusCode = get(err, 'response.status');
    console.error('Service error:', err);

    if (/^4\d+/.test(responseStatusCode)) {
      setFormValidationError('Sorry, This address is not yet available for Schedule & Save orders.');
    } else {
      setFormValidationError(
        'Apologies, our site is currently experiencing technical difficulties, please try again later.',
      );
    }
  };

  const getstoresByAddress = (address: Address) =>
    invoke(preferenceContext, 'storeZipCodeLookup', address).then((response) => {
      const stores = response.data.stores;
      const validationStatuses = response?.data?.validationStatuses || [];
      const firstStore = stores && stores.length > 0 ? stores[0] : null;
      return {
        address: response.data.address,
        storeId: firstStore ? firstStore.locationId : null,
        isAutoReplenishmentEnabled: firstStore?.ecomStore?.storeFeatures?.isAutoReplenishmentEnabled || false,
        validationStatuses: validationStatuses,
      };
    });

  const isAddressValid = (validationPayload) =>
    invoke(preferenceContext, 'validateAddress', validationPayload).then(
      (response) => !isEmpty(response.data.addresses),
    );

  const validateNewAddress = async (address) => {
    console.log('validateNewAddress called with:', address);
    setFormValidationError('');

    const existingPurposes = updatingAddress ? get(updatingAddress, 'purposes') : [{ name: "DELIVERY"}]; // Default to delivery
    const validationPayload = getValidationPayload(address, existingPurposes);
    console.log('validationPayload:', validationPayload);

    try {
      const userAddress: Address = {
        addressLine1: address.streetAddress,
        addressLine2: address.aptSuite,
        city: address.city,
        postalCode: address.zipCode,
        stateCode: address.state,
      };
      console.log('Calling getstoresByAddress with:', userAddress);

      const storesByAddressResp:any = await getstoresByAddress(userAddress);

      console.log('getstoresByAddress response:', storesByAddressResp);
      const foundStoreId = storesByAddressResp?.storeId;
      const validationStatuses = storesByAddressResp?.validationStatuses;
      const isAutoReplenishmentEnabled = storesByAddressResp?.isAutoReplenishmentEnabled;
      console.log('storesByAddressResp?.address?.postalCode', storesByAddressResp?.address?.postalCode)
      const zipCodeExtn = storesByAddressResp?.address?.postalCode?.split('-')[1] || '9999';	// "94566-5973"
      

      const geoCodes =  { latitude: storesByAddressResp?.address?.latitude || '', 
                          longitude: storesByAddressResp?.address?.longitude || '' 
                        };

      console.log('foundStoreId:', foundStoreId, 'validationStatuses:', validationStatuses, 'isAutoReplenishmentEnabled:', isAutoReplenishmentEnabled);

      // Error precedence order:
      // 1. Invalid address (CCVS1003)
      const hasInvalidAddress = validationStatuses?.some(status => status.code === 'CCVS1003');
      if (hasInvalidAddress) {
        setFormValidationError('The address entered cannot be verified with U.S Postal Service records, please try again.');
        return;
      }

      // 2. No store ID (missing stores[0].locationId)
      if (!foundStoreId) {
        setFormValidationError('Sorry, This address is not yet available for Schedule & Save orders.');
        return;
      }

      // 3. isAutoReplenishmentEnabled flag check
      if (!isAutoReplenishmentEnabled) {
        setFormValidationError('Sorry, This address is not yet available for Schedule & Save orders.');
        return;
      }

      // 4. Partial address (CCVS5039) - Show warning on first attempt only
      const hasIncompleteAddress = validationStatuses?.some(status => status.code === 'CCVS5039') && !address.aptSuite;
      if (hasIncompleteAddress && !addressValidationResponse) {
        console.log('First attempt with incomplete address, showing warning');
        setAddressValidationResponse([...validationStatuses]);
        return; // Block first attempt
      }

      // Check if address has been corrected (CCVS1002) and grab the corrected address
      // const addressCorrectedStatus = validationStatuses?.find(status => status.code === 'CCVS1002' && status.type === 'CHANGE');
      let correctedAddress:any  = null;
      // removed this addressCorrectedStatus && , the above condition is violating for 3231 Vineyard Ave Spc 93
      if (storesByAddressResp?.address) {
        // console.log('Address corrected by service, original address:', address, 'corrected address from response:', storesByAddressResp.address);
        correctedAddress = {
          address1: storesByAddressResp.address.addressLine1 || address.streetAddress,
          address2: storesByAddressResp.address.addressLine2 || address.aptSuite,
          city: storesByAddressResp.address.city || address.city,
          state: storesByAddressResp.address.stateCode || address.state,
          zipCode: storesByAddressResp.address.postalCode?.split('-')[0] || address.zipCode,
        };
        console.log('Address corrected, using corrected address:', correctedAddress);
      }

      // Always append addressValidationStatus to the validation payload
      validationPayload.addressValidationStatus = {
        statuses: validationStatuses,
      };

      validationPayload.zipCodeExtn = zipCodeExtn || '';
      validationPayload.geoCodes = geoCodes;

      // If address was corrected, update validationPayload with corrected address values
      if (correctedAddress) {
        validationPayload.address1 = correctedAddress.address1;
        validationPayload.address2 = correctedAddress.address2;
        validationPayload.city = correctedAddress.city;
        validationPayload.state = correctedAddress.state;
        validationPayload.zipCode = correctedAddress.zipCode;
      }

      let isAddressValidResult;
      // This Address validation call is present, to preserve the address during any error in the slots 
      try {
        console.log('Calling isAddressValid with:', validationPayload);
        isAddressValidResult = await isAddressValid(validationPayload);
        console.log('isAddressValid result:', isAddressValidResult);
        // if the customer is updating the selected address then we need to call fetchPreferences to refresh the selected address
        if (updatingAddress && get(updatingAddress, 'addressId') === get(selectedDeliveryAddress, 'addressId')) {
          await invoke(preferenceContext, 'fetchPreferences');
        }

      } catch (err) {
        const responseStatusCode = get(err, 'response.status');
        const errorCode = get(err, 'response.data.errors.0.code');

        if (includes([401, 403], responseStatusCode)) {
          setFormValidationError(
            'Apologies, our site is currently experiencing technical difficulties, please try again later.',
          );
        } else if (!!errorCode) {
          if (includes(['OSAR-ARO-0014', 'IAUC0051'], errorCode)) {
            setFormValidationError(
              'The address entered cannot be verified with U.S Postal Service records, please try again.',
            );
          } else if (errorCode === 'IAUC0090') {
            setFormValidationError(get(err, 'response.data.errors.0.message', 'Apologies, our site is currently experiencing technical difficulties, please try again later.'));
          } else {
            setFormValidationError('Sorry, this address is not yet available for Schedule & Save orders.');
          }
        } else {
          setFormValidationError(
            'Apologies, our site is currently experiencing technical difficulties, please try again later.',
          );
        }
      }

      if (isAddressValidResult) {
        console.log('Address is valid, proceeding to slots');
        setAddressStoreId(foundStoreId);
        
        // Use corrected address if available, otherwise use the original address
        const addressToUse = correctedAddress || {
          address1: address.streetAddress,
          address2: address.aptSuite || '',
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
        };
        
        setSelectedAddress({
          addressId: address.addressId || '',
          address1: addressToUse.address1,
          address2: addressToUse.address2,
          addressType: address.placeType === 'Residential' ? 'RESIDENTIAL' : 'BUSINESS',
          city: addressToUse.city,
          state: addressToUse.state,
          zipCode: addressToUse.zipCode,
        });
        setSavingChanges(true);
        analyticsEvents();
      } else {
        console.log('Address is not valid');
      }
    } catch (err) {
      console.log('validateNewAddress outer error:', err);
      handleServiceError(err);
    }
  };

  useEffect(() => {
    if (!isEmpty(get(slotsInfo, 'error'))) {
      invoke(modalContext, 'setModalError', AppLabels.DeliveryTabForm.LOADING_ERROR);
    }
  }, [get(slotsInfo, 'error')]);

  if (savingChanges) {
    currentTab = 'slots';
  } else if (isLoadingAddresses) {
    currentTab = 'loading';
  } else if (addingNewAddress || (!isLoadingAddresses && isEmpty(addresses))) {
    currentTab = 'newAddress';
  } else if (has(updatingAddress, 'addressId')) {
    currentTab = 'updatingAddress';
  } else {
    currentTab = 'list';
  }

  useEffect(() => {
    if (currentTab === 'slots') {
      setGlobalAriaLiveMessage('');
      setGlobalAriaLiveMessage('select fulfillment date and time preferences');
      invoke(modalContext, 'setScrollable', true);
    } else if (currentTab === 'loading') {
      // setGlobalAriaLiveMessage(`loading list of delivery addresses`);
      invoke(modalContext, 'setScrollable', false);
    } else if (currentTab === 'newAddress') {
      setGlobalAriaLiveMessage('');
      setGlobalAriaLiveMessage('Fill in the form to add a new delivery address');
      invoke(modalContext, 'setScrollable', true);
    } else if (currentTab === 'updatingAddress') {
      setGlobalAriaLiveMessage('');
      setGlobalAriaLiveMessage('Fill in the form to edit the selected delivery address');
      invoke(modalContext, 'setScrollable', true);
    } else {
      // setGlobalAriaLiveMessage(`displaying list of ${addresses.length} addresses`);
      invoke(modalContext, 'setScrollable', false);
    }
  }, [currentTab]);

  const tabLabel =
    currentTab === 'slots'
      ? 'Select preferred slot'
      : currentTab === 'newAddress'
        ? 'Adding a new address'
        : currentTab === 'updatingAddress'
          ? 'Updating address'
          : 'Select address';

  const tabContent = cond([
    [
      () => currentTab === 'slots',
      () => {
        return (
          <PreferencesContext.Provider
            value={{
              fetchSlotsInfo,
              slotsInfo,
              updateSlotsInfo,
            }}
          >
            <SlotPreferences isSelectionCheckEnabled={false} />
          </PreferencesContext.Provider>
        );
      },
    ],
    [
      () => currentTab === 'newAddress',
      () => {
        return (
          <div className="item-list-container">
            <DeliveryTabForm
              onBack={() => {
                setAddingNewAddress(false);
                setFormValidationError('');
                setAddressValidationResponse(null);
              }}
              onSubmitForm={async (address) => {
                await validateNewAddress(address);
              }}
              prefilledAddressValues={updatingAddress}
              validationResponse={addressValidationResponse}
            >
              {!isEmpty(addressValidationError) ? (
                <UserMessage messageText={addressValidationError} messageType={'warning'} />
              ) : null}
            </DeliveryTabForm>
          </div>
        );
      },
    ],
    [
      () => currentTab === 'updatingAddress',
      () => {
        return (
          <div className="item-list-container">
            <DeliveryTabForm
              onBack={() => {
                setUpdatingAddress(null);
                setAddressValidationResponse(null);
              }}
              onSubmitForm={async (address) => {
                return await validateNewAddress({
                  ...address,
                  addressId: get(updatingAddress, 'addressId'),
                });
              }}
              prefilledAddressValues={updatingAddress}
              validationResponse={addressValidationResponse}
            >
              {!isEmpty(addressValidationError) ? (
                <UserMessage messageText={addressValidationError} messageType={'warning'} />
              ) : null}
            </DeliveryTabForm>
          </div>
        );
      },
    ],
    [
      () => true,
      () => {
        return (
          <div className="item-list-container">
            <div className={addressListStyle}>{isLoadingAddresses ? <Loader /> : addresses}</div>

            <button
              aria-label="Add new address"
              aria-roledescription="button"
              className={addAddressSectionStyle}
              onClick={() => setAddingNewAddress(true)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2.25 11.9999C2.25 12.4141 2.58579 12.7499 3 12.7499H21C21.4142 12.7499 21.75 12.4141 21.75 11.9999C21.75 11.5857 21.4142 11.2499 21 11.2499H3C2.58579 11.2499 2.25 11.5857 2.25 11.9999Z"
                  fill="#7B1A27"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 21.75C12.4142 21.75 12.75 21.4142 12.75 21V3C12.75 2.58579 12.4142 2.25 12 2.25C11.5858 2.25 11.25 2.58579 11.25 3V21C11.25 21.4142 11.5858 21.75 12 21.75Z"
                  fill="#7B1A27"
                />
              </svg>
              <p
                className={css`
                  margin-left: 0.5rem;
                `}
              >
                Add new address
              </p>
            </button>

            <div className={footerSectionStyle}>
              <input
                className={submitButtonStyle}
                type="submit"
                value="Save and continue"
                disabled={!get(selectedAddress, 'addressId')}
                onClick={() => {
                  setSavingChanges(true);
                  analyticsEvents();
                }}
              />
            </div>
          </div>
        );
      },
    ],
  ]);

  return (
    <div className={generalStyle} aria-atomic={false} aria-label={tabLabel}>
      {tabContent()}
    </div>
  );
};

DeliveryAddressList.propTypes = {
  onSave: PropTypes.func,
};

export default DeliveryAddressList;
